window.AlardFavourites =
  window.AlardFavourites ||
  (() => {
    const storageKey = 'alard:favourites:v1';
    const changeEventName = 'alard:favourites:change';
    let memoryItems = [];

    const normalizeItems = (value) =>
      Array.isArray(value)
        ? value.filter((item) => item && item.productId).map((item) => ({ ...item, productId: String(item.productId) }))
        : [];

    const read = () => {
      try {
        const storedItems = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
        memoryItems = normalizeItems(storedItems);
      } catch (error) {
        // localStorage may be unavailable in strict privacy contexts.
      }

      return [...memoryItems];
    };

    const notify = (items) => {
      window.dispatchEvent(new CustomEvent(changeEventName, { detail: { items: [...items] } }));
    };

    const write = (items) => {
      memoryItems = normalizeItems(items);

      try {
        window.localStorage.setItem(storageKey, JSON.stringify(memoryItems));
      } catch (error) {
        // Keep favourites for the current page session if storage is blocked.
      }

      notify(memoryItems);
      return [...memoryItems];
    };

    const includes = (productId) => read().some((item) => item.productId === String(productId));

    const toggle = (item) => {
      const items = read();
      const productId = String(item.productId);
      const existingIndex = items.findIndex((favourite) => favourite.productId === productId);
      let isFavourite = true;

      if (existingIndex >= 0) {
        items.splice(existingIndex, 1);
        isFavourite = false;
      } else {
        items.push({ ...item, productId, addedAt: new Date().toISOString() });
      }

      return { items: write(items), isFavourite };
    };

    const update = (item) => {
      const items = read();
      const productId = String(item.productId);
      const existingIndex = items.findIndex((favourite) => favourite.productId === productId);

      if (existingIndex < 0) return items;

      items[existingIndex] = { ...items[existingIndex], ...item, productId };
      return write(items);
    };

    const syncHeader = (items = read()) => {
      const count = items.length;

      document.querySelectorAll('[data-favourites-count]').forEach((countElement) => {
        countElement.textContent = String(count);
        countElement.hidden = count === 0;
      });

      document.querySelectorAll('[data-favourites-link]').forEach((link) => {
        link.setAttribute('aria-label', count ? `Favourites (${count})` : 'Favourites');
      });
    };

    window.addEventListener(changeEventName, (event) => syncHeader(event.detail?.items));
    window.addEventListener('storage', (event) => {
      if (event.key !== storageKey) return;

      try {
        memoryItems = normalizeItems(JSON.parse(event.newValue || '[]'));
      } catch (error) {
        memoryItems = [];
      }
      notify(memoryItems);
    });
    document.addEventListener('shopify:section:load', () => syncHeader());

    syncHeader();

    return { changeEventName, includes, read, toggle, update, write };
  })();

if (!customElements.get('best-seller-card-alard')) {
  class BestSellerCardAlard extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;
      this.variantButtons = Array.from(this.querySelectorAll('[data-variant-id]'));
      this.onCardClick = this.handleCardClick.bind(this);
      this.onFavouritesChange = this.syncFavouriteState.bind(this);
      this.addEventListener('click', this.onCardClick);
      window.addEventListener(window.AlardFavourites.changeEventName, this.onFavouritesChange);
      this.syncFavouriteState();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onCardClick);
      window.removeEventListener(window.AlardFavourites.changeEventName, this.onFavouritesChange);
      this.isInitialized = false;
    }

    handleCardClick(event) {
      const favouriteButton = event.target.closest('[data-favourite-toggle]');
      if (favouriteButton && this.contains(favouriteButton)) {
        event.preventDefault();
        this.toggleFavourite();
        return;
      }

      const variantButton = event.target.closest('[data-variant-id]');
      if (variantButton && this.contains(variantButton)) this.selectVariant(variantButton);
    }

    getFavouriteItem() {
      const productImage = this.querySelector('.best-seller-card-alard__image');
      const productLink = this.querySelector('[data-card-product-link]');

      return {
        productId: this.dataset.productId,
        handle: this.dataset.productHandle,
        title: this.dataset.productTitle,
        url: productLink?.href || this.dataset.productUrl,
        image: productImage?.currentSrc || productImage?.src || this.dataset.currentImage,
        variantId: this.dataset.selectedVariantId,
        price: this.dataset.currentPrice,
      };
    }

    toggleFavourite() {
      if (!this.dataset.productId) return;
      window.AlardFavourites.toggle(this.getFavouriteItem());
    }

    syncFavouriteState() {
      const favouriteButton = this.querySelector('[data-favourite-toggle]');
      if (!favouriteButton || !this.dataset.productId) return;

      const isFavourite = window.AlardFavourites.includes(this.dataset.productId);
      const title = this.dataset.productTitle || 'this product';
      const label = isFavourite ? `Remove ${title} from favourites` : `Add ${title} to favourites`;

      favouriteButton.classList.toggle('is-favourite', isFavourite);
      favouriteButton.setAttribute('aria-pressed', String(isFavourite));
      favouriteButton.setAttribute('aria-label', label);
      favouriteButton.title = isFavourite ? 'Remove from favourites' : 'Add to favourites';
    }

    selectVariant(selectedButton) {
      const variantInput = this.querySelector('.product-variant-id');
      const price = this.querySelector('[data-card-price]');
      const comparePrice = this.querySelector('[data-card-compare-price]');
      const addButton = this.querySelector('[type="submit"]');
      const addButtonText = addButton?.querySelector('span');
      const productImage = this.querySelector('.best-seller-card-alard__image');
      const isAvailable = selectedButton.dataset.available === 'true';
      const variantId = selectedButton.dataset.variantId;

      this.variantButtons.forEach((button) => {
        const isSelected = button === selectedButton;
        button.classList.toggle('is-active', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
      });

      if (variantInput) {
        variantInput.value = variantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (price) price.textContent = selectedButton.dataset.price;

      if (productImage && selectedButton.dataset.imageSrc) {
        productImage.src = selectedButton.dataset.imageSrc;
        productImage.removeAttribute('srcset');
      }

      if (this.dataset.productUrl && variantId) {
        const variantUrl = `${this.dataset.productUrl}${this.dataset.productUrl.includes('?') ? '&' : '?'}variant=${variantId}`;
        this.querySelectorAll('[data-card-product-link]').forEach((link) => {
          link.href = variantUrl;
        });
      }

      this.dataset.selectedVariantId = variantId;
      this.dataset.currentPrice = selectedButton.dataset.price;
      if (selectedButton.dataset.imageSrc) this.dataset.currentImage = selectedButton.dataset.imageSrc;

      if (comparePrice) {
        comparePrice.textContent = selectedButton.dataset.comparePrice || '';
        comparePrice.classList.toggle('is-hidden', !selectedButton.dataset.comparePrice);
      }

      if (addButton) {
        addButton.disabled = !isAvailable;
        if (isAvailable) {
          addButton.removeAttribute('aria-disabled');
        } else {
          addButton.setAttribute('aria-disabled', 'true');
        }
      }
      if (addButtonText) addButtonText.textContent = isAvailable ? 'Add to cart' : 'Sold out';

      if (window.AlardFavourites.includes(this.dataset.productId)) {
        window.AlardFavourites.update(this.getFavouriteItem());
      }
    }
  }

  customElements.define('best-seller-card-alard', BestSellerCardAlard);
}
